import {
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  CalendarClock,
  FileUp,
  FileCheck,
  Music,
  Camera,
  Zap,
  Repeat,
  Radio,
  Timer,
  Send,
  MessageSquare,
  ShieldCheck,
} from "lucide-react"
import type { DeliveryStatus, ArtifactCategory, PaymentAutomation, RecurringTrigger } from "@/types/delivery-manager.types"
import { AlertCircleIcon, ClockIcon } from "@/views/dashboard/components/icons"

// ═══════════════════════════════════════════════════════════════════════
//  Display Config
// ═══════════════════════════════════════════════════════════════════════

export const statusConfig: Record<DeliveryStatus, { label: string; icon: any; className: string }> = {
  pending: { label: "Pending", icon: ClockIcon, className: "text-yellow-400 bg-yellow-400/10" },
  in_progress: { label: "In Progress", icon: ArrowUpRight, className: "text-blue-400 bg-blue-400/10" },
  completed: { label: "Completed", icon: CheckCircle2, className: "text-emerald-400 bg-emerald-400/10" },
  overdue: { label: "Overdue", icon: AlertCircleIcon, className: "text-red-400 bg-red-400/10" },
  upcoming: { label: "Upcoming", icon: CalendarClock, className: "text-violet-400 bg-violet-400/10" },
}

export const automationConfig: Record<PaymentAutomation["type"], { label: string; icon: typeof Zap; className: string }> = {
  on_delivery: { label: "On Delivery", icon: Zap, className: "text-amber-400 bg-amber-400/10 border-amber-400/20" },
  on_date: { label: "Scheduled", icon: Timer, className: "text-blue-400 bg-blue-400/10 border-blue-400/20" },
  recurring: { label: "Recurring", icon: Repeat, className: "text-violet-400 bg-violet-400/10 border-violet-400/20" },
  streaming: { label: "Streaming", icon: Radio, className: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" },
  requires_approval: { label: "Requires Approval", icon: ShieldCheck, className: "text-orange-400 bg-orange-400/10 border-orange-400/20" },
}

export const frequencyLabels: Record<RecurringTrigger["frequency"], string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
}

export const categoryIcons: Record<ArtifactCategory, typeof FileUp> = {
  document: FileCheck,
  audio: Music,
  video: FileUp,
  image: Camera,
}

export const methodLabels: Record<string, string> = {
  wire: "Wire Transfer",
  ach: "ACH Transfer",
  check: "Check",
  platform_escrow: "Platform Escrow",
}

export const approvalStatusConfig: Record<string, { label: string; className: string }> = {
  pending_review: { label: "Pending Review", className: "text-yellow-400 bg-yellow-400/10" },
  approved: { label: "Approved", className: "text-emerald-400 bg-emerald-400/10" },
  rejected: { label: "Rejected", className: "text-red-400 bg-red-400/10" },
  revision_requested: { label: "Revision Requested", className: "text-amber-400 bg-amber-400/10" },
}

export const activityIcons: Record<string, typeof Clock> = {
  created: Clock,
  status_changed: ArrowUpRight,
  uploaded: FileUp,
  approved: CheckCircle2,
  rejected: AlertCircle,
  payment_submitted: Send,
  payment_processed: CheckCircle2,
  comment_added: MessageSquare,
  automation_triggered: Zap,
  reminder_sent: Clock,
}

// ═══════════════════════════════════════════════════════════════════════
//  Shared Formatters
// ═══════════════════════════════════════════════════════════════════════

export function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString()}`
}

/** Short display date (e.g. "Mar 15") */
export function shortDate(iso: string): string {
  const d = new Date(iso + "T00:00:00")
  return d.toLocaleString("en-US", { month: "short", day: "numeric" })
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

export function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(1)} KB`
  return `${bytes} B`
}
